import React, { useMemo } from 'react';
import { Col, convertHourMin, Row } from '../../lib/utils';
import { Body3Regular, CaptionRegular, Subtitle1Bold } from '../../lib/font';
import Grid, { GridColProps } from '../Grid/Grid';
import { MemberScheduleDataType } from '../../services/useMembershipQueryService';
import dayjs from 'dayjs';
import SvgIcon from '../SvgIcon';
import { colors } from '../../lib/colors';
import { useNavigate } from 'react-router-dom';

const GROUP_HISTORY_GRID_WIDTH = 1400;

const getGroupHistoryActionLabel = (status?: string) => {
    if (status === 'CONFIRMED') return '관리';
    if (status === 'WAITLISTED') return '대기 취소';
    if (status === 'COMPLETED' || status === 'NOSHOW') return '상태 수정';
    return '확인';
};

const EllipsisCell = ({
    children,
    title,
    align = 'center',
}: {
    children: React.ReactNode;
    title?: string;
    align?: 'left' | 'center' | 'right';
}) => (
    <div
        title={title}
        style={{
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: align,
            fontSize: '1.6rem',
            fontWeight: 400,
            lineHeight: '2.4rem',
            color: colors.black,
        }}
    >
        {children}
    </div>
);

type Props = {
    /** 종료된 수강권 상세에는 이 값이 오지 않는다. `archivedSchedules` 를 대신 쓴다. */
    schedules?: MemberScheduleDataType[];
    /**
     * 종료(소프트 삭제)된 수강권의 수업 이력. **읽기 전용으로만 그린다.**
     *
     * 서버가 종료된 수강권에 `schedules` 를 안 내리는 것은, 그 배열을 받는 즉시 클릭 가능한
     * 행이 되어 일정 수정·취소 모달까지 이어지기 때문이다(지금까지 상세가 404 라 없던
     * 진입점이다). 같은 내용을 이 키로 받아 목록만 보여준다. 일정 수정은 캘린더에서 한다.
     */
    archivedSchedules?: MemberScheduleDataType[];
    /** 종료된 수강권인가. 행 클릭과 수업 등록 동선을 함께 닫는다. */
    isLifecycleEnded?: boolean;
    memberId: string;
    coachId: string;
    coachName: string;
    membershipId: string;
    onClickRow?: (row: any) => void;
    /** 'group'이면 '수업 등록 바로가기' 버튼 숨김 (그룹 등록 플로우는 별도) */
    source?: 'pt' | 'group';
    /**
     * 공유 수강권이면 여러 회원·강사의 수업이 한 목록에 섞인다.
     * 이때만 '회원'·'강사' 열을 켠다. 개인 수강권은 전 행이 같은 값이라 소음이다.
     */
    isSharedPool?: boolean;
    /** 응답에 회원명이 없을 때 쓸 소유자 이름. */
    ownerName?: string;
    isReadOnly?: boolean;
    /** 사용 기록 제목 줄 오른쪽의 보조 동작. */
    headerRight?: React.ReactNode;
    /** 제목 줄 바로 아래, 표 위에 펼칠 내용. */
    headerContent?: React.ReactNode;
};

const MemberMembershipManageHistory = ({
    schedules,
    archivedSchedules,
    isLifecycleEnded = false,
    memberId,
    coachId,
    coachName,
    membershipId,
    onClickRow,
    source = 'pt',
    isSharedPool = false,
    ownerName = '',
    isReadOnly = false,
    headerRight,
    headerContent,
}: Props) => {
    const isGroup = source === 'group';
    const navigate = useNavigate();
    // 종료된 수강권은 조작 동선을 전부 닫는다. 탈퇴 회원과 같은 규칙을 재사용한다.
    const isRowActionLocked = isReadOnly || isLifecycleEnded;
    const rows = useMemo(() => {
        // 종료된 수강권에는 schedules 가 오지 않는다. 보관 이력을 대신 그린다.
        const data = schedules ?? archivedSchedules ?? [];
        if (!isGroup) return data;

        return [...data].sort(
            (a, b) => dayjs(b.startTime).valueOf() - dayjs(a.startTime).valueOf()
        );
    }, [archivedSchedules, isGroup, schedules]);

    // 수업 등록 바로가기
    const onClickMoveToClassRegister = () => {
        // coachId, coachName, scheduleStartTime
        const selectedSchedule = {
            memberId: memberId,
            coachId: coachId,
            coachName: coachName,
            membershipId: membershipId,
            scheduleStartTime: dayjs().format('YYYY-MM-DD HH:mm'),
        };
        const calendarType = 'daily';
        // 오늘날짜
        const calendarSelectedDay = dayjs().format('YYYY-MM-DD');
        const calendarScrollY = 0;

        navigate('/schedule/create', {
            state: {
                type: 'oneSchedule',
                selectedSchedule: selectedSchedule,
                calendarType,
                calendarSelectedDay,
                calendarScrollY,
                returnPath: '/member/detail/membership',
                returnState: {
                    membershipId,
                    memberId,
                    coachId,
                    coachName,
                    source,
                },
            },
        });
    };
    const colDefs = useMemo<GridColProps[]>(() => {
        if (isGroup) {
            const bookingStatusConfig: Record<
                string,
                { label: string; bg: string; color: string }
            > = {
                CONFIRMED: { label: '예약', bg: '#EFF6FF', color: '#1D4ED8' },
                COMPLETED: { label: '출석', bg: '#D1FAE5', color: '#065F46' },
                NOSHOW: { label: '노쇼', bg: '#FEE2E2', color: '#991B1B' },
                WAITLISTED: { label: '대기', bg: '#FEF3C7', color: '#92400E' },
                CANCELLED: { label: '취소', bg: '#F3F4F6', color: '#6B7280' },
            };

            const countImpactConfig: Record<
                string,
                { bg: string; color: string; border?: string }
            > = {
                deducted: { bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
                restored: { bg: '#ECFDF5', color: '#047857', border: '#A7F3D0' },
                none: { bg: '#F8FAFC', color: '#475569', border: '#CBD5E1' },
                unknown: { bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
            };

            return [
                {
                    field: 'startTime',
                    headerName: '날짜',
                    align: 'center',
                    type: 'date',
                    dateFormat: 'YYYY-MM-DD',
                    width: 140,
                },
                // 공유 그룹 수강권은 참여자 예약이 한 목록에 섞인다. "누가" 없이는
                // 회차 영향(차감/복원)이 어느 회원 것인지 알 수 없다. PT 그리드와 동일 정책.
                ...(isSharedPool
                    ? ([
                          {
                              field: 'memberName',
                              headerName: '회원',
                              align: 'center',
                              width: 130,
                              customComponent: (value: any, row: any) => {
                                  const name =
                                      String(value || row?.memberName || '').trim() || ownerName;
                                  const text = !name
                                      ? '-'
                                      : String(row?.memberId || '') === String(memberId)
                                      ? `${name} (소유자)`
                                      : name;
                                  return (
                                      <EllipsisCell title={text} align="center">
                                          {text}
                                      </EllipsisCell>
                                  );
                              },
                          },
                      ] as GridColProps[])
                    : []),
                {
                    field: 'endTime',
                    headerName: '수업 시간',
                    align: 'center',
                    width: 330,
                    customComponent: (_value, row) => {
                        const text = `${convertHourMin(row.startTime, 'text', true)} ~ ${convertHourMin(
                            row.endTime,
                            'text',
                            true
                        )} (${dayjs(row.endTime).diff(dayjs(row.startTime), 'minute')}분)`;
                        return (
                            <Body3Regular
                                title={text}
                                style={{
                                    color: colors.black,
                                    whiteSpace: 'nowrap',
                                    lineHeight: '2.4rem',
                                }}
                            >
                                {text}
                            </Body3Regular>
                        );
                    },
                },
                {
                    field: 'sessionTitle',
                    headerName: '수업명',
                    align: 'left',
                    width: 290,
                    nullValue: '-',
                    customComponent: (value) => {
                        const text = value || '-';
                        return <EllipsisCell title={text} align="left">{text}</EllipsisCell>;
                    },
                },
                {
                    field: 'bookingStatus',
                    headerName: '상태',
                    align: 'center',
                    width: 100,
                    customComponent: (value) => {
                        const cfg = bookingStatusConfig[value] || bookingStatusConfig.CANCELLED;
                        return (
                            <div className="flex w-full justify-center">
                                <Col
                                    className="flex flex-col justify-center rounded-[4px] px-[10px] py-[6px]"
                                    style={{ backgroundColor: cfg.bg }}
                                >
                                    <CaptionRegular color={cfg.color}>{cfg.label}</CaptionRegular>
                                </Col>
                            </div>
                        );
                    },
                },
                {
                    field: 'countImpactLabel',
                    headerName: '회차 영향',
                    align: 'center',
                    width: 240,
                    customComponent: (value, row) => {
                        const cfg =
                            countImpactConfig[row?.countImpactType || 'unknown'] ||
                            countImpactConfig.unknown;
                        return (
                            <div className="flex w-full justify-center">
                                <span
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        padding: '4px 10px',
                                        borderRadius: 999,
                                        backgroundColor: cfg.bg,
                                        color: cfg.color,
                                        border: cfg.border ? `1px solid ${cfg.border}` : 'none',
                                        lineHeight: 1.4,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {value || '영향 정보 없음'}
                                </span>
                            </div>
                        );
                    },
                },
                {
                    field: 'bookedAt',
                    headerName: '처리 시각',
                    align: 'center',
                    width: 190,
                    customComponent: (_value, row) => {
                        let text = '-';
                        if (row?.bookingStatus === 'CANCELLED' && row?.cancelledAt) {
                            text = `취소 ${dayjs(row.cancelledAt).format('MM-DD HH:mm')}`;
                        } else if (row?.bookedAt) {
                            text = `예약 ${dayjs(row.bookedAt).format('MM-DD HH:mm')}`;
                        }
                        return <EllipsisCell title={text}>{text}</EllipsisCell>;
                    },
                },
                {
                    field: '_id',
                    headerName: '작업',
                    align: 'center',
                    width: 110,
                    customComponent: (_value, row) => {
                        const label = isRowActionLocked
                            ? '조회 전용'
                            : getGroupHistoryActionLabel(row?.bookingStatus);
                        const isActionable = !isRowActionLocked && row?.bookingStatus !== 'CANCELLED';
                        return (
                            <div className="flex w-full justify-center">
                                <span
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        padding: '5px 10px',
                                        borderRadius: 6,
                                        backgroundColor: isActionable ? '#F0FDF4' : '#F8FAFC',
                                        color: isActionable ? '#047857' : '#64748B',
                                        border: `1px solid ${isActionable ? '#BBF7D0' : '#E2E8F0'}`,
                                        lineHeight: 1.3,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {label}
                                </span>
                            </div>
                        );
                    },
                },
            ];
        }

        return [
            {
                field: 'startTime',
                headerName: '날짜',
                align: 'center',
                type: 'date',
                dateFormat: 'YYYY-MM-DD',
            },
            // 공유 수강권에서만 노출한다. "누가" 를 모르면 공용 회차가 어떻게 쓰였는지 알 수 없다.
            ...(isSharedPool
                ? ([
                      {
                          field: 'memberName',
                          headerName: '회원',
                          align: 'center',
                          customFormat: (value: any, row: any) => {
                              const name = String(value || row?.memberName || '').trim() || ownerName;
                              if (!name) return '-';
                              // 소유자와 참여자를 한눈에 구분한다.
                              return String(row?.memberId || '') === String(memberId)
                                  ? `${name} (소유자)`
                                  : name;
                          },
                      },
                      {
                          field: 'coachName',
                          headerName: '강사',
                          align: 'center',
                          customFormat: (value: any) => String(value || '').trim() || coachName || '-',
                      },
                  ] as GridColProps[])
                : []),
            {
                field: 'endTime',
                headerName: '수업 시간',
                align: 'center',
                customFormat: (_value, row) => {
                    return `${convertHourMin(row.startTime, 'text', true)} ~ ${convertHourMin(
                        row.endTime,
                        'text',
                        true
                    )} (${dayjs(row.endTime).diff(dayjs(row.startTime), 'minute')}분)`;
                },
            },
            {
                field: 'currentSession',
                headerName: '회차',
                align: 'center',
                customFormat: (value, row) => {
                    return `${value}/${row.totalSession} 회`;
                },
            },
            {
                field: 'attendanceStatus',
                headerName: '상태',
                align: 'center',
                booleanComponent: {
                    fn: (row) => row.attendanceStatus === 'ATTENDANCE',
                    trueComponent: (
                        <div className="flex w-full justify-center">
                            <Col className="flex flex-col justify-center bg-SUCCESS_50 w-[46px] h-[30px] rounded-[4px]">
                                <CaptionRegular className="text-SUCCESS">출석</CaptionRegular>
                            </Col>
                        </div>
                    ),
                    falseComponent: (
                        <div className="flex w-full justify-center">
                            <Col className="flex flex-col justify-center bg-ERROR_50 w-[46px] h-[30px] rounded-[4px]">
                                <CaptionRegular className="text-ERROR">노쇼</CaptionRegular>
                            </Col>
                        </div>
                    ),
                    etcFn: (row) =>
                        row.attendanceStatus === 'ATTENDANCE' && dayjs(row.startTime).isAfter(dayjs()),
                    etcTrueComponent: (
                        <div className="flex w-full justify-center">
                            <Col className="flex flex-col justify-center bg-BLUE_50 w-[46px] h-[30px] rounded-[4px]">
                                <CaptionRegular className="text-BLUE_500">예정</CaptionRegular>
                            </Col>
                        </div>
                    ),
                },
            },
        ];
    }, [isGroup, isRowActionLocked, isSharedPool, ownerName, memberId, coachName]);

    return (
        <Col>
            <Row className="w-full items-center">
                <Subtitle1Bold className="mr-[12px]">
                    {isGroup ? '회차 영향 기록' : '수강권 사용 기록'}
                </Subtitle1Bold>
                {isLifecycleEnded && (
                    <span className="rounded-[4px] border border-solid border-[#E2E8F0] bg-[#F1F5F9] px-[8px] py-[3px] text-[12px] font-semibold leading-[1.4] text-[#475569]">
                        읽기 전용
                    </span>
                )}
                {!isGroup && !isRowActionLocked && (
                    <Row className="flex items-center cursor-pointer " onClick={onClickMoveToClassRegister}>
                        <Body3Regular className=" text-BLUE_500 mr-[8px]">수업 등록 바로가기</Body3Regular>
                        <SvgIcon
                            name={'SvgRightStrokeArrow'}
                            size={14}
                            fill={colors.trans}
                            stroke={colors.BLUE_500}
                            strokeWidth={1.2}
                        />
                    </Row>
                )}
                {headerRight && <div className="ml-auto shrink-0 pl-[16px]">{headerRight}</div>}
            </Row>
            {headerContent}
            {isLifecycleEnded ? (
                <CaptionRegular color={colors.LAVEL_2} className="mt-[8px] mb-[12px]">
                    종료된 수강권의 보관 기록입니다. 일정 수정·취소는 캘린더에서 해주세요.
                </CaptionRegular>
            ) : (
                isGroup && (
                    <CaptionRegular color={colors.LAVEL_2} className="mt-[8px] mb-[12px]">
                        {isReadOnly
                            ? '탈퇴 당시의 예약 및 회차 영향 기록입니다.'
                            : '예약 확정 시 회차가 먼저 차감됩니다. 행을 선택하면 예약 취소, 대기 취소, 출석 상태 보정을 처리할 수 있습니다.'}
                    </CaptionRegular>
                )
            )}

            <Grid
                colDefs={colDefs}
                rowData={rows}
                isInfinity={true}
                onClickRow={isRowActionLocked ? undefined : onClickRow}
                tableWidth={isGroup ? GROUP_HISTORY_GRID_WIDTH : undefined}
            />
        </Col>
    );
};

export default MemberMembershipManageHistory;
